import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { useAuth } from "../auth/AuthContext";
import { SearchBar } from "../components/SearchBar";

function boolLabel(active) {
  return active ? "Active" : "Inactive";
}

export default function CountryLabelsAdmin() {
  const { authFetch } = useAuth();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  const [creating, setCreating] = useState(false);
  const [newCode, setNewCode] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [newActive, setNewActive] = useState(true);

  const [editingCode, setEditingCode] = useState(null);
  const [editingValues, setEditingValues] = useState({ code: "", label: "", active: true });
  const [savingCode, setSavingCode] = useState(null);

  const loadItems = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await authFetch("/api/auth/admin/country-labels");
      if (!response.ok) {
        let msg = "Error loading country labels";
        try {
          const data = await response.json();
          msg = data?.error || msg;
        } catch {}
        throw new Error(msg);
      }
      const data = await response.json();
      setItems(Array.isArray(data?.labels) ? data.labels : []);
    } catch (err) {
      console.error("Load country labels error:", err);
      setError(err?.message || "Error loading country labels");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const term = search.trim().toLowerCase();
    return items.filter((it) => {
      return (
        String(it.code || "").toLowerCase().includes(term) ||
        String(it.label || "").toLowerCase().includes(term) ||
        boolLabel(!!it.active).toLowerCase().includes(term)
      );
    });
  }, [items, search]);

  const handleCreate = async () => {
    const code = newCode.trim().toUpperCase();
    const label = newLabel.trim();

    if (!code || !label) {
      await Swal.fire({
        icon: "warning",
        title: "Warning",
        text: "Code and label are required.",
        confirmButtonColor: "#3085d6",
      });
      return;
    }

    try {
      setCreating(true);
      const response = await authFetch("/api/auth/admin/country-labels", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code, label, active: newActive }),
      });

      if (!response.ok) {
        let msg = "Error creating country label";
        try {
          const data = await response.json();
          msg = data?.error || msg;
        } catch {}
        throw new Error(msg);
      }

      const data = await response.json();
      const created = data?.label;
      if (created) {
        setItems((prev) => [created, ...prev].sort((a, b) => String(a.code).localeCompare(String(b.code))));
      }
      setNewCode("");
      setNewLabel("");
      setNewActive(true);

      await Swal.fire({
        icon: "success",
        title: "Created",
        text: "Country label created successfully.",
        confirmButtonColor: "#3085d6",
      });
    } catch (err) {
      console.error("Create country label error:", err);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: err?.message || "Error creating country label",
        confirmButtonColor: "#3085d6",
      });
    } finally {
      setCreating(false);
    }
  };

  const beginEdit = (item) => {
    setEditingCode(item.code);
    setEditingValues({
      code: String(item.code || ""),
      label: String(item.label || ""),
      active: !!item.active,
    });
  };

  const handleSave = async (originalCode) => {
    const code = String(editingValues.code || "").trim().toUpperCase();
    const label = String(editingValues.label || "").trim();

    if (!code || !label) {
      await Swal.fire({
        icon: "warning",
        title: "Warning",
        text: "Code and label are required.",
        confirmButtonColor: "#3085d6",
      });
      return;
    }

    try {
      setSavingCode(originalCode);

      const response = await authFetch(`/api/auth/admin/country-labels/${encodeURIComponent(originalCode)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code, label, active: !!editingValues.active }),
      });

      if (!response.ok) {
        let msg = "Error updating country label";
        try {
          const data = await response.json();
          msg = data?.error || msg;
        } catch {}
        throw new Error(msg);
      }

      const data = await response.json();
      const updated = data?.label;
      if (updated) {
        setItems((prev) =>
          prev
            .map((it) => (String(it.code) === String(originalCode) ? updated : it))
            .sort((a, b) => String(a.code).localeCompare(String(b.code))),
        );
      }
      setEditingCode(null);

      await Swal.fire({
        icon: "success",
        title: "Saved",
        text: "Country label updated successfully.",
        confirmButtonColor: "#3085d6",
      });
    } catch (err) {
      console.error("Update country label error:", err);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: err?.message || "Error updating country label",
        confirmButtonColor: "#3085d6",
      });
    } finally {
      setSavingCode(null);
    }
  };

  const handleDeactivate = async (code) => {
    const confirm = await Swal.fire({
      icon: "warning",
      title: "Confirm",
      text: `Delete ${code}?`,
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      confirmButtonColor: "#3085d6",
    });

    if (!confirm.isConfirmed) return;

    try {
      setSavingCode(code);
      const response = await authFetch(`/api/auth/admin/country-labels/${encodeURIComponent(code)}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        let msg = "Error deleting country label";
        try {
          const data = await response.json();
          msg = data?.error || msg;
        } catch {}
        throw new Error(msg);
      }

      await loadItems();

      await Swal.fire({
        icon: "success",
        title: "Done",
        text: "Country label deleted.",
        confirmButtonColor: "#3085d6",
      });
    } catch (err) {
      console.error("Delete country label error:", err);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: err?.message || "Error deleting country label",
        confirmButtonColor: "#3085d6",
      });
    } finally {
      setSavingCode(null);
    }
  };

  if (loading) {
    return (
      <div className="w-full p-6 flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading country labels...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h3 className="text-red-800 font-semibold mb-2">Error</h3>
          <p className="text-red-700 mb-4">{error}</p>
          <button
            onClick={loadItems}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full p-4 md:p-6 font-montserrat space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Country Labels</h2>
        <p className="text-gray-600">Add, edit and deactivate country and region labels.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg shadow-sm p-4">
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Search by code, label or status..."
          />
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <input
              className="rounded border border-slate-300 px-3 py-2 text-sm"
              placeholder="Code (e.g. LAC)"
              value={newCode}
              onChange={(e) => setNewCode(e.target.value.toUpperCase())}
              maxLength={20}
            />
            <input
              className="rounded border border-slate-300 px-3 py-2 text-sm md:col-span-2"
              placeholder="Label"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
            />
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={newActive}
                onChange={(e) => setNewActive(e.target.checked)}
              />
              Active
            </label>
          </div>
          <div className="mt-3">
            <button
              onClick={handleCreate}
              disabled={creating}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
            >
              {creating ? "Creating..." : "Add label"}
            </button>
          </div>
        </div>
      </div>

      <div className="min-h-0">
        <div className="bg-white rounded-lg shadow-sm h-full overflow-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Code</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Label</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filtered.map((item) => {
                const isEditing = editingCode === item.code;
                const isSaving = savingCode === item.code;
                return (
                  <tr key={item.code} className="hover:bg-gray-50 transition">
                    <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                      {isEditing ? (
                        <input
                          className="rounded border border-slate-300 px-2 py-1 text-sm w-24"
                          value={editingValues.code}
                          onChange={(e) =>
                            setEditingValues((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))
                          }
                          maxLength={20}
                        />
                      ) : (
                        item.code
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700 min-w-[260px]">
                      {isEditing ? (
                        <input
                          className="rounded border border-slate-300 px-2 py-1 text-sm w-full"
                          value={editingValues.label}
                          onChange={(e) =>
                            setEditingValues((prev) => ({ ...prev, label: e.target.value }))
                          }
                        />
                      ) : (
                        item.label
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {isEditing ? (
                        <label className="inline-flex items-center gap-2 text-slate-700">
                          <input
                            type="checkbox"
                            checked={!!editingValues.active}
                            onChange={(e) =>
                              setEditingValues((prev) => ({ ...prev, active: e.target.checked }))
                            }
                          />
                          Active
                        </label>
                      ) : (
                        <span
                          className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${item.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700"}`}
                        >
                          {boolLabel(!!item.active)}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm whitespace-nowrap space-x-2">
                      {isEditing ? (
                        <>
                          <button
                            onClick={() => handleSave(item.code)}
                            disabled={isSaving}
                            className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                          >
                            {isSaving ? "Saving..." : "Save"}
                          </button>
                          <button
                            onClick={() => setEditingCode(null)}
                            disabled={isSaving}
                            className="px-3 py-1 bg-gray-400 text-white rounded hover:bg-gray-500 disabled:opacity-50"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => beginEdit(item)}
                            className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                          >
                            Edit
                          </button>
                          {item.active ? (
                            <button
                              onClick={() => handleDeactivate(item.code)}
                              disabled={isSaving}
                              className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                            >
                              {isSaving ? "Working..." : "Delete"}
                            </button>
                          ) : null}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {filtered.length === 0 && (
            <div className="px-6 py-12 text-center text-gray-500">
              <p className="text-lg">No labels found</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
